// Default Libraries
import { createActions, asyncAction } from '../helpers/redux';
import { push } from 'react-router-redux';
import { schema, normalize } from 'normalizr';
import Immutable, { Map } from 'immutable';
import { ElementActions } from './element';

import element2Schema from '../schemas/element';

const elementTypeSchema = new schema.Entity('elementTypes');
// const elementSchema = new schema.Entity('elements');

export const ElementTypeActions = createActions({
  @asyncAction()
  async getElementsByType(elementTypeId) {
    return ({ client }) => {
      return client
        .get(`/element_types/${elementTypeId}/elements`)
        .then(response => {
          response.forEach(entity => {
            entity.relationships = [
              ...entity.consumesFrom,
              ...entity.providesTo,
            ];
            return entity;
          });
          return normalize(response, [element2Schema]);
          // return normalize(response, [elementSchema]);
        });
    };
  },

  @asyncAction()
  async getProjectElementTypes(projectId) {
    return ({ client }) => {
      return client
        .get(`/project/${projectId}/element_types`)
        .then(response => {
          return normalize(response, [elementTypeSchema]);
        });
    };
  },

  @asyncAction()
  async renameElementType(elementTypeId, typeName) {
    return ({ client }) => {
      return client
        .patch(`/element_types/${elementTypeId}`, { body: { type: typeName } })
        .then(response => {
          return normalize(response, elementTypeSchema);
        });
    };
  },

  @asyncAction()
  async getElementType(elementTypeId) {
    return ({ client }) => {
      return client.get(`/element_types/${elementTypeId}`).then(response => {
        return normalize(response, elementTypeSchema);
      });
    };
  },

  @asyncAction()
  async createElementType(elementType) {
    return ({ client }) => {
      return client
        .post(`/element_types`, {
          body: elementType,
        })
        .then(response => {
          return normalize(response, elementTypeSchema);
        });
    };
  },

  @asyncAction()
  async saveDescription(elementTypeId, description) {
    return ({ client }) => {
      return client
        .patch(`/element_types/${elementTypeId}`, {
          body: { description: description },
        })
        .then(response => {
          return normalize(response, elementTypeSchema);
        });
    };
  },

  @asyncAction()
  async updateElementTypeProperties(elementType) {
    return async ({ client, getState, Checkit }) => {
      const elementTypeId = elementType.get('id');

      try {
        return client
          .put(`/element_types/${elementTypeId}/props`, {
            body: elementType.get('props'),
          })
          .then(response => {
            return response;
          });
      } catch (err) {
        let error;
        if (err instanceof Checkit.Error) {
          error = err.toJSON();
        } else {
          error = err.json.errors;
        }
        throw error;
      }
    };
  },

  @asyncAction()
  async createElementTypeProperty(elementType, props) {
    return async ({ client, getState, Checkit }) => {
      const elementTypeId = elementType.get('id');
      const propName = props.propName;
      const defaultMeta = props.defaultMeta;
      const existingProps = elementType.get('props') || new Map();

      let propData = existingProps.set(propName, Immutable.fromJS(defaultMeta));

      propData = propData.map((prop, key) => {
        if (key !== propName) {
          let order = parseInt(prop.get('_order'), 10);
          return prop.set('_order', order + 1);
        } else {
          return prop;
        }
      });

      const newProps = {
        props: propData.toJS(),
      };
      return client
        .patch(`/element_types/${elementTypeId}`, {
          body: newProps,
        })
        .then(response => {
          return response;
        });
    };
  },

  @asyncAction()
  async deleteElementType(elementType) {
    return async ({ dispatch, client, getState, Checkit }) => {
      return client
        .delete(`/element_types/${elementType.elementTypeId}`)
        .then(response => {
          dispatch(push(`/project/${elementType.currentProjectId}`));
          dispatch(
            ElementTypeActions.clientDeleteElementTypeElements(
              elementType.elementTypeId,
            ),
          );
          return { elementTypeId: elementType.elementTypeId };
        });

      // .then((a) => {
      //   dispatch(pushState(null, `/project/${elementType.projectId}/default`));
      // });
      // return client.delete(`/element_types/${elementType.elementTypeId}/props/${elementType.prop}`).then((response) => {
      //   let rsp = response;
      //   rsp.elementtype_id = elementType.elementTypeId;
      //   rsp.deleted_key = elementType.prop;
      //   return rsp;
      // });
    };
  },

  clientDeleteElementTypeElements(elementTypeId) {
    return ({ getState, dispatch }) => {
      let elementIds = getState()
        .elements.get('elements')
        .filter((v, k) => {
          return v.get('type_id') === elementTypeId;
        })
        .keySeq()
        .toArray();

      elementIds.forEach(v => {
        dispatch(ElementActions.clientDeleteElements(v));
      });

      return {};
    };
  },

  updatePropsOrder(elementTypeId, dragKey, hoverKey) {
    return {
      elementTypeId,
      dragKey,
      hoverKey,
    };
  },

  @asyncAction()
  async deleteElementTypeProperty(elementType) {
    return async ({ client, getState, Checkit }) => {
      return client
        .delete(
          `/element_types/${elementType.elementTypeId}/props/${elementType.prop}`,
        )
        .then(response => {
          let rsp = response;
          rsp.elementtype_id = elementType.elementTypeId;
          rsp.deleted_key = elementType.prop;
          return rsp;
        });
    };
  },

  @asyncAction()
  async renameElementTypeProperty(elementType) {
    return async ({ client, getState, Checkit }) => {
      return client
        .patch(
          `/element_types/${elementType.elementTypeId}/props/${elementType.prop}/rename`,
          {
            body: elementType,
          },
        )
        .then(response => {
          let rsp = response;
          rsp.elementtype_id = elementType.elementTypeId;
          rsp.deleted_key = elementType.prop;
          rsp.new_key = elementType.newPropKey;
          return rsp;
        });
    };
  },

  @asyncAction()
  async saveElementTypeProperty(elementType, propKey, meta) {
    return async ({ client, getState, Checkit }) => {
      const elementTypeId = elementType.get('id');

      return client
        .patch(`/element_types/${elementTypeId}/props/${propKey}`, {
          body: meta,
        })
        .then(response => {
          return response;
        });
    };
  },

  @asyncAction()
  async createElementTypeMetaProperty(obj) {
    return async ({ client, getState, Checkit }) => {
      return client
        .post(`/element_types/${obj.elementTypeId}/props/${obj.prop}`, {
          body: obj,
        })
        .then(response => {
          let rsp = response;
          rsp.elementtype_id = obj.elementTypeId;
          rsp.prop = obj.prop;
          rsp.meta_key = obj.metaKey;
          rsp.meta_value = obj.metaValue;
          return rsp;
        });
    };
  },

  @asyncAction()
  async renameElementTypeMetaProperty(obj) {
    return async ({ client, getState, Checkit }) => {
      return client
        .patch(
          `/element_types/${obj.elementTypeId}/props/${obj.prop}/${obj.metaKey}/rename`,
          {
            body: obj,
          },
        )
        .then(response => {
          let rsp = {};
          rsp.elementtype_id = obj.elementTypeId;
          rsp.prop = obj.prop;
          rsp.deleted_meta_key = obj.metaKey;
          rsp.new_meta_key = obj.newMetaKey;
          return rsp;
        });
    };
  },

  @asyncAction()
  async deleteElementTypeMetaProperty(obj) {
    return async ({ client, getState, Checkit }) => {
      return client
        .delete(
          `/element_types/${obj.elementTypeId}/props/${obj.prop}/${obj.metaKey}`,
        )
        .then(response => {
          let rsp = response;
          rsp.elementtype_id = obj.elementTypeId;
          rsp.prop = obj.prop;
          rsp.meta_key = obj.metaKey;
          return rsp;
        });
    };
  },
});



// WEBPACK FOOTER //
// ./src/actions/elementType.js